import React, { createContext, useContext, useEffect, useState } from "react";
import {
  setAuthHeader,
  clearAuthHeader,
  refreshRequest,
  logoutRequest,
} from "../api/auth";

const AuthContext = createContext(null);

const SAP_PORTAL_ACCESS_TOKEN_KEY = "aei_portal_access_token";
const PORTAL_LOGOUT_MARKER_KEY = "aei_portal_logout_at";

function clearSap2000PortalToken() {
  try {
    sessionStorage.removeItem(SAP_PORTAL_ACCESS_TOKEN_KEY);
  } catch (err) {
    console.warn("Could not clear SAP2000 portal token:", err);
  }
}

function markPortalLogout() {
  try {
    localStorage.setItem(PORTAL_LOGOUT_MARKER_KEY, String(Date.now()));
  } catch (err) {
    console.warn("Could not mark portal logout:", err);
  }
}

function clearPortalLogoutMarker() {
  try {
    localStorage.removeItem(PORTAL_LOGOUT_MARKER_KEY);
  } catch (err) {
    console.warn("Could not clear portal logout marker:", err);
  }
}

export function AuthProvider({ children }) {
  const [authReady, setAuthReady] = useState(false);
  const [auth, setAuth] = useState({
    user: null,
    accessToken: null,
  });

  const applyAuthState = ({ user, accessToken }) => {
    const nextAuth = {
      user: user || null,
      accessToken: accessToken || null,
    };

    if (nextAuth.accessToken) {
      clearPortalLogoutMarker();
      setAuthHeader(nextAuth.accessToken);
    } else {
      clearAuthHeader();
    }

    setAuth(nextAuth);
    return nextAuth;
  };

  const clearSession = ({ markLogout = false } = {}) => {
    clearAuthHeader();
    clearSap2000PortalToken();

    if (markLogout) {
      markPortalLogout();
    }

    setAuth({
      user: null,
      accessToken: null,
    });
  };

  const refreshSession = async () => {
    try {
      const fresh = await refreshRequest();

      const nextAuth = applyAuthState({
        user: fresh.user || null,
        accessToken: fresh.accessToken || null,
      });

      return nextAuth;
    } catch (err) {
      clearSession({ markLogout: true });
      throw err;
    }
  };

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      try {
        const fresh = await refreshRequest();

        if (cancelled) return;

        applyAuthState({
          user: fresh.user || null,
          accessToken: fresh.accessToken || null,
        });
      } catch (err) {
        if (cancelled) return;
        clearSession();
      } finally {
        if (!cancelled) {
          setAuthReady(true);
        }
      }
    }

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (authPayload) => {
    if (authPayload?.user && authPayload?.accessToken) {
      const nextAuth = applyAuthState({
        user: authPayload.user,
        accessToken: authPayload.accessToken,
      });
      setAuthReady(true);
      return nextAuth;
    }

    const fresh = await refreshSession();
    setAuthReady(true);
    return fresh;
  };

  const logout = async () => {
    try {
      await logoutRequest();
    } catch (err) {
      console.warn("Logout request failed:", err);
    } finally {
      clearSession({ markLogout: true });
      setAuthReady(true);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        auth,
        authReady,
        login,
        logout,
        refreshSession,
        clearSession,
        isAuthenticated: Boolean(auth?.user && auth?.accessToken),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);

  if (!ctx) {
    throw new Error("useAuth must be used inside AuthProvider");
  }

  return ctx;
}